import { useMemo, useEffect, useState } from "react";
import axios from "axios";
import {
  CircularProgress,
  Pagination,
  Box,
  Chip,
  Modal,
  Typography,
  IconButton,
  Card,
  CardContent,
  Fade,
  Backdrop,
  Switch,
  FormControlLabel,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import CloseIcon from "@mui/icons-material/Close";
import DomainIcon from "@mui/icons-material/Domain";
import PersonIcon from "@mui/icons-material/Person";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import SoftInput from "components/SoftInput";
import CustomdateD from "../Dashboard/CustomdateD";
import dayjs from "dayjs";
import { LAMBDA_BASE_URL } from "../../../constants";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryParams } from "context/QueryParamsContext";
import { enUS } from "date-fns/locale";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import { useAuth } from "context/AuthContext";

function AllUsersTable({
  entriesPerPageOptions = [5, 10, 15, 20, 25],
  canSearch = true,
  date = true,
  isSorted = true,
}) {
  const columns = [
    { Header: "Email", accessor: "email", align: "left" },
    { Header: "Name", accessor: "name", align: "left" },
    { Header: "Whitelabel User", accessor: "is_whitelabel_user", align: "center" },
    { Header: "Domains", accessor: "domain_names", align: "left" },
    { Header: "Created At", accessor: "created_at", align: "left" },
  ];

  const [rows, setRows] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(entriesPerPageOptions[0] || 10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });
  const [togglingUserId, setTogglingUserId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUserDomains, setSelectedUserDomains] = useState([]);
  const [selectedUserInfo, setSelectedUserInfo] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { dateRange, setDateRange } = useQueryParams();
  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const start = (page - 1) * entriesPerPage + 1;
  const end = Math.min(page * entriesPerPage, totalEntries);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
      setSearchLoading(false);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [search]);

  const fetchAllUsers = async () => {
    if (!domainId) return;
    setLoading(true);
    try {
      const { start_date, end_date } = dateRange;
      const offset = (page - 1) * entriesPerPage;
      const res = await axios.get(`${LAMBDA_BASE_URL}/all-users`, {
        params: {
          limit: entriesPerPage,
          offset,
          search: debouncedSearch,
          sort_key: sortConfig.key,
          sort_direction: sortConfig.direction,
          start_date,
          end_date,
        },
        headers: { "x-api-key": process.env.REACT_APP_API_KEY },
      });
      const formattedRows = res.data.result.users.map((user) => ({
        id: user.id,
        email: user.email,
        name: user.name,
        is_whitelabel_user: user.is_whitelabel_user,
        domain_names: user.domain_names || [],
        domain_details: user.domain_details || [],
        created_at: user.created_at,
      }));
      setRows(formattedRows);
      setTotalEntries(res.data.result.total);
    } catch (err) {
      console.error("Error fetching all users", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (domainId) fetchAllUsers();
  }, [page, entriesPerPage, debouncedSearch, sortConfig, dateRange, domainId]);

  const sortedRows = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    return [...rows].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (sortConfig.key === "domain_names") {
        return sortConfig.direction === "asc"
          ? (aVal?.length || 0) - (bVal?.length || 0)
          : (bVal?.length || 0) - (aVal?.length || 0);
      }
      if (sortConfig.key === "is_whitelabel_user") {
        return sortConfig.direction === "asc"
          ? (aVal ? 1 : -1) - (bVal ? 1 : -1)
          : (bVal ? 1 : -1) - (aVal ? 1 : -1);
      }
      if (sortConfig.key === "created_at") {
        return sortConfig.direction === "asc"
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      }
      if (aVal === bVal) return 0;
      return sortConfig.direction === "asc" ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  }, [rows, sortConfig]);

  const updateDomainWhitelabelSettings = async (user, isEnabled) => {
    if (!user.domain_details || user.domain_details.length === 0) return;
    const domainTokens = user.domain_details.map((d) => d.domain_token);
    const updatePromises = domainTokens.map((domainToken) =>
      axios
        .patch(
          `${LAMBDA_BASE_URL}/settings`,
          { is_whitelabelled: isEnabled },
          {
            headers: {
              "domain-token": domainToken,
              "x-api-key": process.env.REACT_APP_API_KEY,
            },
          }
        )
        .catch((err) =>
          console.error(`Failed to update settings for domain ${domainToken}:`, err)
        )
    );
    await Promise.allSettled(updatePromises);
  };

  const handleWhitelabelToggle = async (user, newValue) => {
    setTogglingUserId(user.id);
    try {
      const response = await axios.patch(
        `${LAMBDA_BASE_URL}/user-whitelabel-status`,
        { user_id: user.id, is_whitelabel_user: newValue },
        { headers: { "x-api-key": process.env.REACT_APP_API_KEY } }
      );

      if (response.data.statusCode === 200) {
        await updateDomainWhitelabelSettings(user, newValue);
        await fetchAllUsers();
      } else {
        console.error("Failed to update whitelabel status:", response.data.message);
      }
    } catch (error) {
      console.error("Error updating whitelabel status:", error);
    } finally {
      setTogglingUserId(null);
    }
  };

  const handlePageChange = (event, value) => setPage(value);
  const handleEntriesPerPageChange = ({ value }) => { setEntriesPerPage(value); setPage(1); };
  const handleSearchChange = (event) => { setSearch(event.target.value); setSearchLoading(true); };
  const handleSort = (columnKey) =>
    setSortConfig((prev) => ({
      key: columnKey,
      direction: prev.key === columnKey ? (prev.direction === "desc" ? "asc" : "desc") : "asc",
    }));

  const handleDateRangeSelect = ({ start, end }) => {
    setDateRange({ start_date: start, end_date: end });
    localStorage.setItem("saved_start_date", start);
    localStorage.setItem("saved_end_date", end);
    const queryParams = new URLSearchParams(location.search);
    queryParams.set("start_date", start);
    queryParams.set("end_date", end);
    navigate({ pathname: location.pathname, search: queryParams.toString() });
  };

  const handleViewAllDomains = (user) => {
    setSelectedUserDomains(user.domain_details || []);
    setSelectedUserInfo({ name: user.name, email: user.email, is_whitelabel_user: user.is_whitelabel_user });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTimeout(() => { setSelectedUserDomains([]); setSelectedUserInfo(null); }, 200);
  };

  const renderDomainNames = (domainNames, domainDetails, user) => {
    if (!domainNames || domainNames.length === 0) {
      return (
        <SoftTypography variant="caption" color="textSecondary" fontStyle="italic">
          No domains
        </SoftTypography>
      );
    }
    return (
      <SoftBox display="flex" flexWrap="wrap" gap={0.5} maxWidth="300px">
        {domainNames.slice(0, 3).map((domain, index) => (
          <Chip key={index} label={domain} size="small" variant="outlined"
            sx={{ fontSize: "0.75rem", height: "24px", borderColor: "#219297", color: "#219297", "& .MuiChip-label": { px: 1 } }}
          />
        ))}
        {domainNames.length > 3 && (
          <Chip
            label={`+${domainNames.length - 3} more`}
            size="small" variant="filled"
            onClick={() => handleViewAllDomains(user)}
            sx={{ fontSize: "0.75rem", height: "24px", cursor: "pointer", backgroundColor: "#219297", color: "#ffffff", "& .MuiChip-label": { px: 1 }, "&:hover": { backgroundColor: "#1a7579" } }}
          />
        )}
      </SoftBox>
    );
  };

  return (
    <>
      <TableContainer sx={{ boxShadow: "none", minHeight: "400px" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress sx={{ color: "#219297" }} />
          </Box>
        ) : (
          <>
            {/* Controls */}
            <SoftBox display="flex" flexDirection={{ xs: "column", md: "row" }} flexWrap="wrap"
              justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} gap={2} p={2}>
              <SoftBox display="flex" alignItems="center" sx={{ width: { xs: "100%", md: "auto" } }}>
                <SoftSelect
                  defaultValue={{ value: entriesPerPage, label: entriesPerPage }}
                  options={entriesPerPageOptions.map((e) => ({ value: e, label: e }))}
                  onChange={handleEntriesPerPageChange}
                  size="small"
                />
                <SoftTypography variant="caption" color="secondary" ml={1}>
                  entries per page
                </SoftTypography>
              </SoftBox>
              <SoftBox display="flex" flexDirection={{ xs: "column", md: "row" }}
                alignItems={{ xs: "stretch", md: "center" }} gap={2} width={{ xs: "100%", md: "auto" }}>
                {canSearch && (
                  <SoftBox sx={{ width: { xs: "100%", md: "12rem" }, position: "relative" }}>
                    <SoftInput placeholder="Search users..." value={search} onChange={handleSearchChange} fullWidth />
                    {searchLoading && (
                      <CircularProgress size={20} sx={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", color: "#219297" }} />
                    )}
                  </SoftBox>
                )}
                {date && (
                  <SoftBox sx={{ width: { xs: "100%", md: "auto" } }}>
                    <CustomdateD onRangeSelect={handleDateRangeSelect} />
                  </SoftBox>
                )}
              </SoftBox>
            </SoftBox>

            {/* Table */}
            <Table>
              <SoftBox component="thead">
                {columns.map((column, key) => (
                  <DataTableHeadCell key={key} width={column.width || "auto"} align={column.align || "left"}
                    onClick={() => handleSort(column.accessor)}
                    sorted={sortConfig.key === column.accessor ? sortConfig.direction : "none"}
                    style={{ cursor: "pointer", userSelect: "none" }}>
                    {column.Header}
                  </DataTableHeadCell>
                ))}
              </SoftBox>
              <TableBody>
                {sortedRows.length > 0 ? (
                  sortedRows.map((row, key) => (
                    <TableRow key={key}>
                      {columns.map((col, index) => (
                        <DataTableBodyCell key={index} align={col.align || "left"}>
                          {col.accessor === "created_at" ? (
                            dayjs(row[col.accessor]).format("MMM D, YYYY hh:mm A")
                          ) : col.accessor === "domain_names" ? (
                            renderDomainNames(row[col.accessor], row.domain_details, row)
                          ) : col.accessor === "is_whitelabel_user" ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={row.is_whitelabel_user || false}
                                    disabled={togglingUserId === row.id}
                                    onChange={(e) => handleWhitelabelToggle(row, e.target.checked)}
                                    sx={{
                                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#219297" },
                                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#219297" },
                                    }}
                                  />
                                }
                                label=""
                              />
                              {togglingUserId === row.id ? (
                                <CircularProgress size={16} sx={{ color: "#219297" }} />
                              ) : row.is_whitelabel_user ? (
                                <Chip
                                  label="Enabled"
                                  size="small"
                                  sx={{
                                    fontSize: "0.7rem",
                                    height: "22px",
                                    backgroundColor: "#e8f5f5",
                                    color: "#219297",
                                    border: "1px solid #219297",
                                    fontWeight: 600,
                                    "& .MuiChip-label": { px: 1 },
                                  }}
                                />
                              ) : null}
                            </Box>
                          ) : (
                            row[col.accessor] || "-"
                          )}
                        </DataTableBodyCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <DataTableBodyCell colSpan={columns.length} align="center">
                      <SoftTypography variant="body2" color="textSecondary">No users found</SoftTypography>
                    </DataTableBodyCell>
                  </TableRow>
                )}
              </TableBody>

              {totalEntries > 0 && (
                <SoftBox pb={1} display="flex" justifyContent="space-between" alignItems="center"
                  paddingTop="10px" paddingLeft="40px">
                  <SoftTypography variant="button" color="secondary">
                    Showing {new Intl.NumberFormat(enUS).format(start)} to{" "}
                    {new Intl.NumberFormat(enUS).format(end)} of{" "}
                    {new Intl.NumberFormat(enUS).format(totalEntries)} entries
                  </SoftTypography>
                </SoftBox>
              )}
            </Table>

            {totalPages > 0 && (
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
                <Pagination count={totalPages} page={page} onChange={handlePageChange}
                  shape="circular" color="info"
                  sx={{ "& .MuiPaginationItem-root": { "&.Mui-selected": { background: "#219297", color: "white !important", "&:hover": { background: "#1a7579" } }, "&:not(.Mui-selected)": { color: "black" } } }}
                />
                <SoftTypography variant="button">
                  Page {new Intl.NumberFormat(enUS).format(page)} of{" "}
                  {new Intl.NumberFormat(enUS).format(totalPages)}
                </SoftTypography>
              </SoftBox>
            )}
          </>
        )}
      </TableContainer>

      <Modal open={modalOpen} onClose={handleCloseModal} closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500, sx: { backgroundColor: "rgba(0,0,0,0.5)" } } }}>
        <Fade in={modalOpen}>
          <Box sx={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600, maxWidth: "90vw", maxHeight: "80vh",
            bgcolor: "background.paper",
            boxShadow: "0 20px 60px rgba(33,146,151,0.15)",
            borderRadius: 3, overflow: "hidden",
          }}>
            <Box sx={{ background: "linear-gradient(135deg, #219297 0%, #1a7579 100%)", p: 3, color: "white" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Typography variant="h5" fontWeight="600" color="#ffffff" mb={1}>
                    User Domain Details
                  </Typography>
                  {selectedUserInfo && (
                    <Box sx={{ mt: 1, opacity: 0.95 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <PersonIcon sx={{ fontSize: 16, color: "#ffffff" }} />
                        <Typography variant="body2" fontWeight="500" color="#ffffff">
                          {selectedUserInfo.name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }} color="#ffffff">
                        {selectedUserInfo.email}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }} color="#ffffff">
                        Whitelabel: {selectedUserInfo.is_whitelabel_user ? "Yes" : "No"}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <IconButton onClick={handleCloseModal} size="small"
                  sx={{ color: "#ffffff", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ p: 3, maxHeight: "calc(80vh - 180px)", overflowY: "auto" }}>
              {selectedUserDomains.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary"
                    sx={{ mb: 2, fontSize: "0.875rem", fontWeight: 600 }}>
                    Total Domains: {selectedUserDomains.length}
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {selectedUserDomains.map((domain, index) => (
                      <Card key={index} sx={{
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        border: "1px solid #e0e0e0", borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": { boxShadow: "0 4px 12px rgba(33,146,151,0.15)", borderColor: "#219297", transform: "translateY(-2px)" },
                      }}>
                        <CardContent sx={{ p: 2.5 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box display="flex" alignItems="center" gap={1}>
                              <DomainIcon sx={{ fontSize: 20, color: "#219297" }} />
                              <Typography variant="subtitle1" fontWeight="600" sx={{ color: "#344767" }}>
                                {domain.domain_name}
                              </Typography>
                            </Box>
                            <Chip label={domain.role} size="small"
                              sx={{
                                backgroundColor: domain.role === "admin" ? "#e8f5f5" : "#f5f5f5",
                                color: domain.role === "admin" ? "#219297" : "#67748e",
                                fontWeight: 600, fontSize: "0.75rem", height: "28px",
                                textTransform: "capitalize",
                                border: domain.role === "admin" ? "1px solid #219297" : "1px solid #e0e0e0",
                                "& .MuiChip-label": { px: 1.5 },
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
                  <DomainIcon sx={{ fontSize: 64, color: "#e0e0e0", mb: 2 }} />
                  <Typography variant="body1" color="text.secondary" textAlign="center">
                    No domain details available
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
}

export default AllUsersTable;